interface ContextBreakdown {
    input: number;
    cache_read: number;
    cache_creation: number;
    output: number;
}

interface ContextRequest {
    number: number;
    tokens: number;
    delta: number | null;
    cache_percent: number;
    dropped: boolean;
}

interface SessionUsage {
    input_tokens: number;
    output_tokens: number;
    cache_read_tokens: number;
    cache_creation_tokens: number;
    list_cost: number | null;
    currency: string | null;
    budget: number | null;
    active_seconds: number | null;
}

export interface ContextUsage {
    tokens: number;
    window: number | null;
    percent: number | null;
    baseline?: number;
    model?: string | null;
    compactions?: number;
    breakdown?: ContextBreakdown;
    requests?: ContextRequest[];
    session?: SessionUsage | null;
}

const SYSTEM_COLOR = 'bg-slate-500';
const MESSAGE_COLOR = 'bg-violet-500';
const FREE_COLOR = 'bg-muted';

const formatNumber = (value: number) => value.toLocaleString();

const percentOf = (value: number, scale: number) =>
    scale ? Math.min((value / scale) * 100, 100) : 0;

const formatMoney = (amount: number, currency: string | null) =>
    `${amount.toFixed(2)} ${currency ?? ''}`.trim();

const formatDuration = (totalSeconds: number | null) => {
    if (totalSeconds === null) return '–';
    if (totalSeconds < 60) return `${Math.round(totalSeconds)}s`;
    return `${Math.floor(totalSeconds / 60)}m ${Math.round(
        totalSeconds % 60
    )}s`;
};

const formatChange = (change: number | null) => {
    if (change === null) return '–';
    const sign = change >= 0 ? '+' : '−';
    return `${sign}${formatNumber(Math.abs(change))}`;
};

const Tile = ({
    label,
    value,
    children
}: {
    label: string;
    value: string;
    children?: React.ReactNode;
}) => (
    <div className="p-3 border border-border rounded-lg">
        <div className="text-muted-foreground text-xs">{label}</div>
        <div className="font-semibold tabular-nums text-2xl">{value}</div>
        {children}
    </div>
);

const CategoryLine = ({
    color,
    label,
    tokens,
    scale
}: {
    color: string;
    label: string;
    tokens: number;
    scale: number;
}) => (
    <div className="flex items-center gap-2">
        <span className={`inline-block rounded-sm w-2.5 h-2.5 ${color}`} />
        <span>{label}</span>
        <span className="ml-auto tabular-nums text-muted-foreground">
            {formatNumber(tokens)} - {percentOf(tokens, scale).toFixed(1)}%
        </span>
    </div>
);

export const ContextView = ({ context }: { context?: ContextUsage | null }) => {
    if (!context) {
        return (
            <div className="text-muted-foreground text-sm italic">
                No model request yet in this chat.
            </div>
        );
    }

    const scale = context.window ?? context.tokens;
    const systemTokens = Math.min(context.baseline ?? 0, context.tokens);
    const messageTokens = context.tokens - systemTokens;
    const freeTokens = Math.max(scale - context.tokens, 0);
    const session = context.session;
    const budgetPercent =
        session?.budget && session.list_cost !== null
            ? percentOf(session.list_cost, session.budget)
            : null;

    return (
        <div className="space-y-5">
            <div className="gap-3 grid grid-cols-2 sm:grid-cols-4">
                <Tile
                    label="Context"
                    value={
                        context.percent !== null ? `${context.percent}%` : '–'
                    }
                >
                    <div className="tabular-nums text-muted-foreground text-xs">
                        {formatNumber(context.tokens)}
                        {context.window
                            ? ` / ${formatNumber(context.window)}`
                            : ''}
                    </div>
                </Tile>
                <Tile
                    label="Compactions"
                    value={String(context.compactions ?? 0)}
                >
                    <div className="text-muted-foreground text-xs">
                        this session
                    </div>
                </Tile>
                <Tile
                    label="Session cost"
                    value={
                        session?.list_cost != null
                            ? formatMoney(session.list_cost, session.currency)
                            : '–'
                    }
                >
                    {budgetPercent !== null && session?.budget ? (
                        <div className="space-y-1 mt-1">
                            <div className="bg-muted rounded-full h-1.5 overflow-hidden">
                                <div
                                    className="bg-foreground h-full"
                                    style={{ width: `${budgetPercent}%` }}
                                />
                            </div>
                            <div className="text-muted-foreground text-xs">
                                of{' '}
                                {formatMoney(session.budget, session.currency)}{' '}
                                budget
                            </div>
                        </div>
                    ) : (
                        <div className="text-muted-foreground text-xs">
                            no budget
                        </div>
                    )}
                </Tile>
                <Tile
                    label="Active time"
                    value={formatDuration(session?.active_seconds ?? null)}
                >
                    <div className="text-muted-foreground text-xs">
                        updated when idle
                    </div>
                </Tile>
            </div>

            <div className="space-y-2">
                <div className="flex justify-between text-xs">
                    <span className="font-medium">
                        {context.model ?? 'Unknown model'}
                    </span>
                    <span className="text-muted-foreground">
                        {context.window
                            ? 'Estimated usage by category'
                            : 'Context window unknown for this model'}
                    </span>
                </div>
                <div className="flex bg-muted rounded-md h-5 overflow-hidden">
                    <div
                        className={SYSTEM_COLOR}
                        style={{ width: `${percentOf(systemTokens, scale)}%` }}
                    />
                    <div
                        className={MESSAGE_COLOR}
                        style={{ width: `${percentOf(messageTokens, scale)}%` }}
                    />
                </div>
                <div className="gap-x-6 gap-y-1 grid sm:grid-cols-3 text-xs">
                    <CategoryLine
                        color={SYSTEM_COLOR}
                        label="System, tools & skills"
                        tokens={systemTokens}
                        scale={scale}
                    />
                    <CategoryLine
                        color={MESSAGE_COLOR}
                        label="Messages"
                        tokens={messageTokens}
                        scale={scale}
                    />
                    {context.window ? (
                        <CategoryLine
                            color={FREE_COLOR}
                            label="Free space"
                            tokens={freeTokens}
                            scale={scale}
                        />
                    ) : null}
                </div>
            </div>

            {context.requests && context.requests.length > 0 ? (
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-muted-foreground text-left">
                            <th className="py-1 font-normal">Request</th>
                            <th className="py-1 w-1/2 font-normal">Context</th>
                            <th className="py-1 font-normal">Change</th>
                            <th className="py-1 font-normal">Cached</th>
                        </tr>
                    </thead>
                    <tbody>
                        {context.requests.map((request) => (
                            <tr
                                key={request.number}
                                className="border-border border-t"
                            >
                                <td className="py-1">#{request.number}</td>
                                <td className="py-1 pr-3">
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                                            <div
                                                className={`h-full ${
                                                    request.dropped
                                                        ? 'bg-destructive'
                                                        : MESSAGE_COLOR
                                                }`}
                                                style={{
                                                    width: `${percentOf(
                                                        request.tokens,
                                                        scale
                                                    )}%`
                                                }}
                                            />
                                        </div>
                                        <span className="w-14 tabular-nums text-right">
                                            {formatNumber(request.tokens)}
                                        </span>
                                    </div>
                                </td>
                                <td
                                    className={`py-1 tabular-nums ${
                                        request.dropped
                                            ? 'text-destructive'
                                            : ''
                                    }`}
                                >
                                    {formatChange(request.delta)}
                                    {request.dropped ? ' ↓ compacted?' : ''}
                                </td>
                                <td className="py-1 tabular-nums">
                                    {request.cache_percent}%
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : null}

            {session ? (
                <div className="text-muted-foreground text-xs">
                    Session total: {formatNumber(session.input_tokens)} in -{' '}
                    {formatNumber(session.output_tokens)} out - cache read{' '}
                    {formatNumber(session.cache_read_tokens)} - cache write{' '}
                    {formatNumber(session.cache_creation_tokens)}
                </div>
            ) : null}
        </div>
    );
};
